<template>
  <v-data-table
    :headers="formatHeaders()"
    :items="formatItems(chartObj.data.result)"
    :pagination.sync="chartObj.pagination"
    @update:pagination="updatePagination"
    :total-items="chartObj.pagination.totalItems"
    :rows-per-page-items="[5, 10, 25, 50]"
    :rows-per-page-text="translation('table.rows-per-page-text')"
    :loading="!chartObj.loaded"
    :hide-headers="chartObj.data.result && !chartObj.data.result.length"
    :hide-actions="hidePagination || (chartObj.data.result && !chartObj.data.result.length)"
    class="elevation-1">
    <template slot="headerCell" slot-scope="props">
      <v-tooltip v-if="props.header.tooltip" bottom>
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" class="pr-1 tooltip-icon">info</v-icon>
        </template>
        <span>{{ translation(`data.${chartKey}.delta-tooltip`)}}</span>
      </v-tooltip>
      <template v-if="props.header.tableWithIndicatorColumn">
        <span v-if="props.header.text.includes('_curr')">
          {{ translation(`data.${chartKey}.${props.header.text.split('_')[0]}`) }}
        </span>
        <br/>
        <span>
          {{ translation(`data.${chartKey}.${props.header.text.substring(props.header.text.indexOf('_') + 1, props.header.text.length)}`) }}
        </span>
      </template>
      <template v-else>
        <span>
          {{ props.header.text }}
        </span>
      </template>
    </template>
    <template slot="items" slot-scope="props">
      <tr :class="[
        props.item.name ? addRowClass(props.item.name) : '',
        chartSettings && chartSettings.tableWithTotalRow  && chartObj.data.result && chartObj.data.result.length === props.index ? 'bold' : ''
      ]">
        <td v-for="(header, index) in formatHeaders()" :key="index" class="text-xs-left" :class="addObjClass(header)">
          <template v-if="header.value.includes('_indicator')">
            <v-icon v-if="props.item[header.value] === 1" color="green">arrow_upward</v-icon>
            <v-icon v-if="props.item[header.value] === -1" color="red">arrow_downward</v-icon>
          </template>
          <template v-else>
            {{ translation(`chart.data.value.${props.item[header.value]}`) !== `chart.data.value.${props.item[header.value]}` ? translation(`chart.data.value.${props.item[header.value]}`) : props.item[header.value]}}
          </template>
        </td>
      </tr>
    </template>
    <template slot="footer" v-if="footerData && chartObj.data.result && chartObj.data.result.length">
      <td v-for="(footer, index) in formatFooter()" :key="index" class="text-xs-left px-2 footer-cell">
        <span>{{ footer.name }}</span><br>
        <span>{{ footer.value }}</span>
      </td>
    </template>
    <template slot="no-data">
      <div class="headline pa-3">{{ translation('table.no-table-data') }}</div>
    </template>
    <template slot="pageText" slot-scope="item">
      {{item.pageStart}}-{{item.pageStop}} {{ translation('table.results-of-text') }} {{item.itemsLength}}
    </template>
  </v-data-table>
</template>

<script>
import { eventBus } from '@/eventBus'
import { mapGetters } from 'vuex'
import { accounting } from 'accounting'

export default {
  name: 'chartTableRender',
  props: {
    comparisonPeriod: { type: Boolean, required: false },
    dateTimeComparison: { type: Array, required: false },
    chartKey: { type: String, required: true },
    footerData: { type: Object },
    hidePagination: { type: Boolean }
  },
  data () {
    return {
      chartSettings: {},
      enableUpdatePagination: false,
      totalRow: {}
    }
  },
  computed: {
    ...mapGetters(['translation']),
    chartObj: {
      get () { return this.$store.getters.getChartByKey(this.chartKey) }
    }
  },
  created () {
    this.chartSettings = this.chartObj.chartSettings
    if (this.chartSettings && (this.chartSettings.tableWithIndicator || this.chartSettings.tableWithTotalRow)) {
      eventBus.$on('update-dashboard', () => {
        this.setTotalTableRow()
      })
    }
  },
  watch: {
    dateTimeComparison () {
      if (this.chartSettings && this.chartSettings.tableWithIndicator) {
        this.setTotalTableRow()
      }
    }
  },
  destroyed () {
    this.enableUpdatePagination = false
  },
  methods: {
    addObjClass (obj) {
      if (obj && obj.value) {
        return {
          'pl-2 pr-4': obj.value.includes('Delta'),
          'pl-4 pr-2': !obj.value.includes('Delta')
        }
      }
      return ''
    },
    addRowClass (rowName) {
      if (rowName === 'ConversionRate' || (this.chartSettings && this.chartSettings.tableWithIndicator && rowName === 'Total')) {
        return 'bold'
      }
    },
    formatItems (tableData) {
      if (tableData && tableData.length) {
        if (this.chartSettings && this.chartSettings.chartOrder && this.chartSettings.chartOrder.length && tableData.length === this.chartSettings.chartOrder.length) {
          let reorderedData = []
          tableData.forEach((item) => {
            let newIndex = this.chartSettings.chartOrder.indexOf(item.name)
            if (newIndex !== -1) {
              reorderedData[newIndex] = item
            }
          })
          return reorderedData
        } else if (this.chartSettings && this.chartSettings.tableWithIndicator) {
          const tableColumnsKeys = Object.keys(tableData[0])
          const tableColumnsKeysWithIndicator = []
          const tableDataWithIndicator = []

          tableColumnsKeys.forEach((key) => {
            if (key.includes('_prev')) {
              tableColumnsKeysWithIndicator.push(key.split('_')[0].concat('_indicator'))
            }
          })

          tableData.forEach((item) => {
            const newItem = { ...item }
            tableColumnsKeysWithIndicator.forEach((key) => {
              const keyName = key.split('_')[0]
              const previous = newItem[keyName.concat('_prev')]
              const current = newItem[keyName.concat('_curr')]
              newItem[key] = Math.sign(current - previous)
            })
            tableDataWithIndicator.push(newItem)
          })
          tableDataWithIndicator.push(this.totalRow)
          return tableDataWithIndicator
        } else if (this.chartSettings && this.chartSettings.tableWithTotalRow) {
          const tableColumnsKeys = Object.keys(tableData[0])
          const totalKey = tableColumnsKeys[0]
          this.totalRow[totalKey] = 'Total'
          const tableDataWithTotalRow = [...tableData]
          tableDataWithTotalRow.push(this.totalRow)
          return tableDataWithTotalRow
        } else {
          return tableData.map(item => {
            if (this.chartObj.chartSettings.formatTableNumbers) {
              for (let i in item) {
                if (typeof item[i] === 'number') {
                  let formatPriceParams = {}
                  if (process.env.VUE_APP_FORMAT_NUMBER_PARAMS) {
                    try {
                      formatPriceParams = JSON.parse(process.env.VUE_APP_FORMAT_NUMBER_PARAMS)
                      item[i] = accounting.format(item[i], formatPriceParams)
                    } catch (e) {}
                  }
                }
              }
              // replace Delta zero values with '-'
              if (this.comparisonPeriod && !this.dateTimeComparison.length) {
                const objKeys = Object.keys(item)
                objKeys.forEach(key => {
                  if (key.includes('Delta')) {
                    item[key] = '-'
                  }
                })
              }
            }
            if (item['sort_column']) delete item['sort_column']
            return item
          })
        }
      }
    },
    updatePagination (pagination) {
      let paginationPayload = {
        key: this.chartObj.key,
        pagination: pagination
      }
      // disable 'automatically' invoked method @update:pagination="updatePagination" when the data-table component is mounted
      this.enableUpdatePagination
        ? this.$emit('update-pagination', paginationPayload)
        : this.enableUpdatePagination = true

      if (this.chartSettings && this.chartSettings.tableWithTotalRow) {
        this.setTotalTableRow()
      }
    },
    formatHeaders () {
      if (this.chartObj.data.result) {
        if (this.chartSettings.sortDate) {
          let dateKeys = [...this.$_.keys(this.chartObj.data.result[0])]
          let name = dateKeys.splice(dateKeys.indexOf('name'), 1)[0]
          dateKeys.sort()
          dateKeys.unshift(name)
          return dateKeys.map(item => {
            let text
            if (this.chartObj.data.translations && this.$_.has(this.chartObj.data.translations, item) && this.chartObj.data.translations[item] !== '') {
              text = this.chartObj.data.translations[item]
            } else {
              text = item
            }
            return {
              'text': text,
              'align': 'left',
              'value': item,
              'class': 'pl-4 pr-2',
              'width': 'auto'
            }
          })
        } else if (this.chartSettings && this.chartSettings.tableWithIndicator) {
          const tableColumnsKeys = [...this.$_.keys(this.chartObj.data.result[0])]
          const tableColumnsKeysAll = []
          tableColumnsKeysAll.push('name')
          tableColumnsKeys.forEach((key) => {
            if (key.includes('_prev') || key.includes('_curr')) {
              tableColumnsKeysAll.push(key)
            }
            if (key.includes('_prev')) {
              tableColumnsKeysAll.push(key.split('_')[0].concat('_indicator'))
            }
          })
          return tableColumnsKeysAll.map(item => {
            return {
              'text': item,
              'align': 'left',
              'value': item,
              'class': 'pl-4 pr-2',
              'width': 'auto',
              'tableWithIndicatorColumn': true,
              'sortable': !item.includes('_indicator')
            }
          })
        } else {
          let tableKeys = this.$_.keys(this.chartObj.data.result[0])
          // remove sort_column key if exists
          let sortColumnKeyIndex = tableKeys.findIndex(item => item === 'sort_column')
          if (sortColumnKeyIndex !== -1) {
            tableKeys.splice(sortColumnKeyIndex, 1)
          }
          return tableKeys.map(item => {
            let text
            if (this.chartObj.data.translations && this.$_.has(this.chartObj.data.translations, item) && this.chartObj.data.translations[item] !== '') {
              text = this.chartObj.data.translations[item]
            } else if (item.includes('Delta')) {
              text = `Delta%`
            } else {
              text = item
            }
            return {
              'text': text,
              'align': 'left',
              'value': item,
              'class': item.includes('Delta') ? 'pl-2 pr-4' : 'pl-4 pr-2',
              'width': 'auto',
              'tooltip': !!item.includes('Delta')
            }
          })
        }
      } else {
        return []
      }
    },
    formatFooter () {
      if (this.footerData && this.chartObj.data.result) {
        let footerArray = []
        footerArray.push({ name: '', value: this.translation('table.footer.total') })

        for (let key in this.footerData) {
          let obj = {
            name: this.translation(`chart.title.footer.${this.footerData[key]}`),
            value: ''
          }
          let kpiByKey = this.$store.getters.getChartByKey(this.footerData[key])
          if (kpiByKey && kpiByKey.data.result && kpiByKey.data.result.length) obj.value = kpiByKey.data.result[0].value

          footerArray.push(obj)
        }

        let difference = this.$_.keys(this.chartObj.data.result[0]).length - footerArray.length
        for (let i = 0; i < difference; i++) {
          footerArray.unshift({ name: '', value: '' })
        }
        return footerArray
      }
    },
    setTotalTableRow () {
      this.$store.dispatch('fetchChartDataTotal', {
        key: this.chartSettings.totalRowKey,
        filters: this.$store.getters.isGenericGen ? this.$store.getters.selectedFiltersGen : this.$store.getters.selectedFilters,
        date_time_comparison: this.dateTimeComparison
      }).then(data => {
        if (data.result && data.result.length) {
          this.totalRow = data.result[0]
        }
      })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .footer-cell {
    font-size: 13px;
    font-weight: 600;
  }
  .bold td {
    font-weight: bold !important;
  }

  .tooltip-icon {
    opacity: 1 !important;
    color: rgba(0,0,0,.54) !important;
    font-size: 20px !important;
  }
</style>
